import AddAPhotoOutlinedIcon from '@mui/icons-material/AddAPhotoOutlined';
import Avatar from '@mui/material/Avatar';
import Button from '@mui/material/Button';
import FormHelperText from '@mui/material/FormHelperText';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useId, useState, type ChangeEvent, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

const ACCEPTED_TYPES = 'image/svg+xml,image/png,image/jpeg,image/gif';
const MAX_BYTES = 1024 * 1024;

export type LogoUploadProps = {
    logoUrl?: string;
    onChange: (file: File | undefined) => void;
    helperText?: string;
};

export function LogoUpload({ logoUrl, onChange, helperText }: LogoUploadProps): ReactElement {
    const { t } = useTranslation();
    const errorId = useId();
    const [error, setError] = useState<string | undefined>(undefined);

    const handleChange = (event: ChangeEvent<HTMLInputElement>): void => {
        const file = event.target.files?.[0];
        event.target.value = '';
        if (!file) {
            setError(undefined);
            onChange(undefined);
            return;
        }
        if (file.size > MAX_BYTES) {
            setError(t('SETTINGS.COMPANY.LOGO.SIZE_ERROR'));
            return;
        }
        setError(undefined);
        onChange(file);
    };

    const resolvedHelperText = helperText ?? t('SETTINGS.COMPANY.LOGO.HELPER');

    return (
        <Stack spacing={1}>
            <Stack direction="row" spacing={2} alignItems="center">
                <Avatar
                    src={logoUrl}
                    alt={logoUrl ? t('SETTINGS.COMPANY.LOGO.ALT') : ''}
                    sx={{
                        width: 96,
                        height: 96,
                        bgcolor: 'background.default',
                        color: 'text.secondary',
                        border: '1px dashed',
                        borderColor: 'divider',
                    }}>
                    {!logoUrl && <AddAPhotoOutlinedIcon />}
                </Avatar>
                <Button
                    variant="outlined"
                    size="small"
                    component="label"
                    aria-describedby={error ? errorId : undefined}>
                    {t('SETTINGS.COMPANY.LOGO.UPLOAD')}
                    <input type="file" accept={ACCEPTED_TYPES} hidden onChange={handleChange} />
                </Button>
            </Stack>
            <Typography variant="caption" color="text.secondary" component="p">
                {resolvedHelperText}
            </Typography>
            {error && (
                <FormHelperText id={errorId} role="alert" error>
                    {error}
                </FormHelperText>
            )}
        </Stack>
    );
}
